import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import delay from '../utils/delay';
import { setCreditCardComplete } from '../slices/creditCardComplete';
import { Elements } from '@stripe/react-stripe-js';
import { OrderingSelectors } from 'polygon-ordering';

const { getPaymentGatewayConfig } = OrderingSelectors;

const PaymentWrapper = ({
  children,
  shouldHaveMerchantAccount = false,
}: {
  children: React.ReactNode;
  shouldHaveMerchantAccount?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const paymentGatewayConfig = useAppSelector(getPaymentGatewayConfig);

  const hqGatewayKey = useAppSelector(state => state.config.hqGatewayKey);
  const merchantAccount = useAppSelector(state => state.config.stripeMerchantAccount);
  const [stripe, setStripe] = useState<Stripe | null>(null);

  // We want to use the HQ account for saved cards (CVC).
  // Previously, the default account for all clients was HQ, but that’s no longer always the case.
  // Since the default account can vary, we should explicitly use the HQ account instead.
  // Concept Ate default account is not HQ

  const stripeAccountToUse = paymentGatewayConfig?.merchantAccount || merchantAccount;

  useEffect(() => {
    dispatch(setCreditCardComplete(false));
    readyStripe();
  }, [hqGatewayKey]);

  const readyStripe = () => {
    if (!hqGatewayKey) {
      return;
    }
    setStripe(null);
    delay(1000)
      .then(() =>
        loadStripe(
          hqGatewayKey,
          // The merchant account is required for CVC revalidation (with a saved card).
          // Merchant account is handled on the back end for non-saved card purchases.
          shouldHaveMerchantAccount && stripeAccountToUse
            ? { stripeAccount: stripeAccountToUse }
            : undefined,
        ),
      )
      .then(stripe => setStripe(stripe));
  };
  return (
    <Elements key={hqGatewayKey} stripe={stripe}>
      {children}
    </Elements>
  );
};

export default PaymentWrapper;
